import { Currency } from '@commercetools/frontend-sdk/lib/types/Currency';
import { Params } from 'types/next';
import { cookies } from 'next/headers';
import { useCookies } from 'react-cookie';

interface LocalizationMapping {
  locale: string;
  currency: Currency;
  currencyCode: string;
  countryName: string;
  countryCode: string;
  countryKey: string;
}

const localizationMapper = {
  en: {
    locale: 'en_GB',
    currency: 'GBP',
    currencyCode: '£',
    countryCode: 'GB',
    countryName: 'United Kingdom',
    countryKey: 'gb',
  },
  en_GB: {
    locale: 'en_GB',
    currency: 'GBP',
    currencyCode: '£',
    countryCode: 'GB',
    countryName: 'United Kingdom',
    countryKey: 'gb',
  },
  de: {
    locale: 'en_GB',
    currency: 'EUR',
    currencyCode: '€',
    countryCode: 'DE',
    countryName: 'Germany',
    countryKey: 'de',
  },
  fr: {
    locale: 'en_FR',
    currency: 'EUR',
    currencyCode: '€',
    countryCode: 'FR',
    countryName: 'France',
    countryKey: 'fr',
  },
  it: {
    locale: 'en_IT',
    currency: 'EUR',
    currencyCode: '€',
    countryCode: 'IT',
    countryName: 'Italy',
    countryKey: 'it',
  },
  at: {
    locale: 'en_AT',
    currency: 'EUR',
    currencyCode: '€',
    countryCode: 'AT',
    countryName: 'Austria',
    countryKey: 'at',
  },
  be: {
    locale: 'en_BE',
    currency: 'EUR',
    currencyCode: '€',
    countryCode: 'BE',
    countryName: 'Belgium',
    countryKey: 'be',
  },
  cz: {
    locale: 'en_CZ',
    currency: 'EUR',
    currencyCode: '€',
    countryCode: 'CZ',
    countryName: 'Czech Republic',
    countryKey: 'cz',
  },
  dk: {
    locale: 'en_DK',
    currency: 'EUR',
    currencyCode: '€',
    countryCode: 'DK',
    countryName: 'Denmark',
    countryKey: 'dk',
  },
  fi: {
    locale: 'en_FI',
    currency: 'EUR',
    currencyCode: '€',
    countryCode: 'FI',
    countryName: 'Finland',
    countryKey: 'fi',
  },
  hu: {
    locale: 'en_HU',
    currency: 'EUR',
    currencyCode: '€',
    countryCode: 'HU',
    countryName: 'Hungary',
    countryKey: 'hu',
  },
  is: {
    locale: 'en_IS',
    currency: 'EUR',
    currencyCode: '€',
    countryCode: 'IS',
    countryName: 'Iceland',
    countryKey: 'is',
  },
  ie: {
    locale: 'en_IE',
    currency: 'EUR',
    currencyCode: '€',
    countryCode: 'IE',
    countryName: 'Ireland',
    countryKey: 'ie',
  },
  lu: {
    locale: 'en_LU',
    currency: 'EUR',
    currencyCode: '€',
    countryCode: 'LU',
    countryName: 'Luxembourg',
    countryKey: 'lu',
  },
  nl: {
    locale: 'en_NL',
    currency: 'EUR',
    currencyCode: '€',
    countryCode: 'NL',
    countryName: 'Netherlands',
    countryKey: 'nl',
  },
  pl: {
    locale: 'en_PL',
    currency: 'EUR',
    currencyCode: '€',
    countryCode: 'PL',
    countryName: 'Poland',
    countryKey: 'pl',
  },
  pt: {
    locale: 'en_PT',
    currency: 'EUR',
    currencyCode: '€',
    countryCode: 'PT',
    countryName: 'Portugal',
    countryKey: 'pt',
  },
  sk: {
    locale: 'en_SK',
    currency: 'EUR',
    currencyCode: '€',
    countryCode: 'SK',
    countryName: 'Slovakia',
    countryKey: 'sk',
  },
  es: {
    locale: 'en_ES',
    currency: 'EUR',
    currencyCode: '€',
    countryCode: 'ES',
    countryName: 'Spain',
    countryKey: 'es',
  },
  se: {
    locale: 'en_SE',
    currency: 'EUR',
    currencyCode: '€',
    countryCode: 'SE',
    countryName: 'Sweden',
    countryKey: 'se',
  },
  ch: {
    locale: 'en_CH',
    currency: 'EUR',
    currencyCode: '€',
    countryCode: 'CH',
    countryName: 'Switzerland',
    countryKey: 'ch',
  },
} as Record<string, LocalizationMapping>;

export const getLocalizationInfo = (locale: string) => {
  if (!(locale in localizationMapper)) {
    console.warn(
      `Invalid locale ${locale} provided. Possible values are ${Object.keys(localizationMapper).join(', ')}`,
    );

    return localizationMapper.en;
  }

  return localizationMapper[locale as keyof typeof localizationMapper];
};

export const parseParams = (params: Params) => {
  const { locale: paramsLocale } = params;
  if (!localizationMapper[paramsLocale]) {
    if (typeof params.slug === 'string') {
      params.slug = `${params.locale}/${params.slug}`;
    } else if ((params.slug as string[])?.length) {
      if (paramsLocale) {
        (params.slug as string[]).unshift(paramsLocale);
      }
    } else {
      params.slug = [paramsLocale ?? ''];
    }
    params.locale = 'en';
  }
};
